import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { TourId } from 'webapp/enums/tour';

import ClientsMetricsMainComponent from 'webapp/components/clients/metrics-main';
import ClientsOverviewComponent from 'webapp/components/clients/overview';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';

import type { ClientMetric, Space } from 'webapp/models-custom';
import FilterSortClientInOverview from 'webapp/models-custom/filter-sort/client-in-overview';

interface Args {
	model: Space[];
	onAdd: () => void;
}

export default class ClientsPerformanceComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('tour') declare tourService: Service.Tour;
	@service('model-manager/client-metrics')
	declare mmClientMetricsService: Service.ModelManager.ClientMetrics;
	@service('navigation')
	declare navigationService: Service.Navigation;

	filterSort: FilterSortClientInOverview;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.filterSort = new FilterSortClientInOverview(
			{
				getAllItems: () => {
					return this.args.model;
				},
			},
			false,
		);

		this.checkToShowHelpBubble();
	}

	get addButtonContainerNode() {
		return this.accountService.canAddClient
			? this.navigationService.addButtonContainerNode
			: null;
	}

	@cached
	get clientMetrics() {
		return this.mmClientMetricsService.clientMetricsModels.filter(
			(item) => item.spaceId === this.accountService.id,
		);
	}

	get hasResults() {
		return !this.filterSort?.noResults;
	}

	@cached
	get mainClientMetrics(): ClientMetric[] {
		const items = new Array<ClientMetric>(5);

		this.clientMetrics.forEach((metric) => {
			items[metric.order] = metric;
		});

		return items;
	}

	@action
	checkToShowHelpBubble() {
		if (this.tourService.isEligibleForClientPerformanceTour && !this.clientMetrics.length) {
			scheduleOnce('afterRender', this, this.showHelpBubble);
		}
	}

	@action
	showHelpBubble() {
		const tour = this.tourService.initTour(TourId.CLIENT_PERFORMANCE);
		tour?.start();
	}

	@action
	addClient() {
		this.args.onAdd();
	}
	<template>
		<div id="clients-performance">
			{{#if this.filterSort}}
				<FilterLineProxyComponent @model={{this.filterSort}} />
			{{/if}}

			<div id="favorite-wrapper-position">

				<div id="favorite-wrapper-scroll">

					{{#if this.hasResults}}
						<div id="mainFavorites">

							<div class="container">
								<ul>
									{{#each this.mainClientMetrics as |metric index|}}
										<ClientsMetricsMainComponent
											@model={{metric}}
											@position={{index}}
										/>
									{{/each}}
								</ul>
							</div>

						</div>
					{{/if}}

					<div id="clientFavorites">
						<ClientsOverviewComponent
							@model={{@model}}
							@isClientMetrics={{true}}
							@filterSort={{this.filterSort}}
						/>
					</div>
				</div>
			</div>

			{{#if this.addButtonContainerNode}}
				{{#in-element this.addButtonContainerNode}}
					<CommonAddButtonComponent @text="Add account" @callTrigger={{this.addClient}} />
				{{/in-element}}
			{{/if}}
		</div>
	</template>
}
